import {
  restoreErrorMessage,
  useArchivedCanvases,
  useArchivedChats,
  useArchivedFiles,
  useArchivedSkills,
  useArchivedWorkspaces,
  useRecoverFileRecord,
  useRestoreCanvas,
  useRestoreFlowSession,
  useRestoreSkill,
  useRestoreWorkspace,
} from "@/api/archived";
import { useWorkspaces } from "@/api/workspaces";
import {
  chipButtonClassName,
  EmptySearch,
  EmptyState,
  PaneBody,
  PaneHead,
  Row,
} from "@/components/AdminPanel/Organization/components/manage-pane-primitives";
import { SkillNameConflictDialog } from "@/components/SkillNameConflictDialog/SkillNameConflictDialog";
import { Button } from "@/components/ui/button";
import { cn } from "@/utils";
import { nameTakenSuggestion } from "@/utils/skillNameConflict";
import { FileRecordTypeIcon, Spinner } from "@cohesive/ui-components";
import { AxiosError } from "axios";
import { differenceInCalendarDays, formatDistanceToNow } from "date-fns";
import {
  ArchiveRestore,
  FileText,
  Frame,
  Layers,
  Lock,
  MessagesSquare,
  ScrollText,
} from "lucide-react";
import { ReactNode, useMemo, useState } from "react";
import toast from "react-hot-toast";

/**
 * Archive — everything you have put away, and the one verb that brings it
 * back. Rows name where they came from so Restore is never a guess.
 *
 * Archiving is the reversible move; there is no permanent delete here because
 * the API has none yet. A row whose parent is also archived says so rather
 * than offering a Restore that would fail.
 */

/** Restorable window for soft-deleted S3 files; mirrors the backend's query window. */
const FILE_RESTORE_WINDOW_DAYS = 14;

type ArchiveKind = "canvas" | "chat" | "file" | "skill" | "workspace";

const KIND_META: Record<
  ArchiveKind,
  { noun: string; scope: string; icon: ReactNode }
> = {
  canvas: {
    noun: "Canvas",
    scope: "Canvases",
    icon: <Frame className="size-4" />,
  },
  file: { noun: "File", scope: "Files", icon: <FileText className="size-4" /> },
  chat: {
    noun: "Chat",
    scope: "Chats",
    icon: <MessagesSquare className="size-4" />,
  },
  skill: {
    noun: "Skill",
    scope: "Skills",
    icon: <ScrollText className="size-4" />,
  },
  workspace: {
    noun: "Workspace",
    scope: "Workspaces",
    icon: <Layers className="size-4" />,
  },
};

type ArchiveEntry = {
  id: string;
  kind: ArchiveKind;
  name: string;
  /** Where it came from — the restore target, in words. */
  where?: string;
  archivedAt?: string;
  icon?: ReactNode;
  extraMeta?: string;
  blockedReason?: string;
  onRestore: () => void;
};

function archivedAgo(ts?: string): string {
  if (!ts) return "archived";
  return `archived ${formatDistanceToNow(new Date(ts), { addSuffix: true })}`;
}

function fileDaysLeft(deletedAt?: string): string {
  if (!deletedAt) return "";
  const daysGone = differenceInCalendarDays(new Date(), new Date(deletedAt));
  const left = Math.max(FILE_RESTORE_WINDOW_DAYS - daysGone, 0);
  return left === 1 ? "1 day left" : `${left} days left`;
}

export function ArchivePane({ scope = "All" }: { scope?: string }) {
  const workspacesQuery = useArchivedWorkspaces();
  const canvasesQuery = useArchivedCanvases();
  const chatsQuery = useArchivedChats();
  const filesQuery = useArchivedFiles();
  const skillsQuery = useArchivedSkills();
  const { data: liveWorkspaces } = useWorkspaces();

  const [restoringId, setRestoringId] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [skillConflict, setSkillConflict] = useState<{
    skillId: string;
    originalName: string;
    suggestedName: string;
  } | null>(null);

  const restoreWorkspace = useRestoreWorkspace({
    onSuccess: () => toast.success("Restored workspace."),
    onError: (err) =>
      toast.error(restoreErrorMessage(err, "Failed to restore workspace")),
  });
  // 409s carry "restore the parent first" guidance — restores never cascade
  // upward, so a canvas or chat whose parent is also archived points at the
  // parent instead.
  const restoreCanvas = useRestoreCanvas({
    onSuccess: () => toast.success("Restored canvas."),
    onError: (err) =>
      toast.error(restoreErrorMessage(err, "Failed to restore canvas")),
  });
  const restoreChat = useRestoreFlowSession({
    onSuccess: () => toast.success("Restored chat."),
    onError: (err) =>
      toast.error(restoreErrorMessage(err, "Failed to restore chat")),
  });
  const recoverFile = useRecoverFileRecord({
    onSuccess: () => toast.success("Restored file."),
    onError: () => toast.error("Failed to restore file"),
  });

  const skills = skillsQuery.data?.pages.flatMap((p) => p.data.skills) ?? [];
  const restoreSkill = useRestoreSkill({
    onSuccess: () => {
      setSkillConflict(null);
      toast.success("Restored skill.");
    },
    onError: (err: AxiosError, variables) => {
      // A name collision (the archived name was reused) opens the keep-both
      // dialog. Use the mutation's own variables so the dialog always targets
      // the skill that actually failed.
      const suggestedName = nameTakenSuggestion(err);
      if (suggestedName !== null) {
        const originalName =
          skills.find((sk) => sk.id === variables.skillId)?.name ??
          suggestedName.replace(/-\d+$/, "");
        setSkillConflict({
          skillId: variables.skillId,
          originalName,
          suggestedName,
        });
        return;
      }
      toast.error(restoreErrorMessage(err, "Failed to restore skill"));
    },
  });

  /** Origin workspace names, for rows that carry a workspace id. */
  const workspaceNames = useMemo(() => {
    const map = new Map<string, string>();
    for (const workspace of liveWorkspaces ?? []) {
      map.set(workspace.id, workspace.name || "Untitled");
    }
    return map;
  }, [liveWorkspaces]);

  const entries: ArchiveEntry[] = useMemo(() => {
    const workspaces =
      workspacesQuery.data?.pages.flatMap((p) => p.data.workspaces) ?? [];
    const canvases =
      canvasesQuery.data?.pages.flatMap((p) => p.data.canvases) ?? [];
    const chats =
      chatsQuery.data?.pages.flatMap((p) => p.data.flow_sessions) ?? [];
    const files =
      filesQuery.data?.pages.flatMap((p) => p.data.file_records) ?? [];

    const all: ArchiveEntry[] = [
      ...workspaces.map((ws) => ({
        id: ws.id,
        kind: "workspace" as const,
        name: ws.name || "Untitled",
        where: "Organization",
        archivedAt: ws.archived_at,
        onRestore: () => {
          setRestoringId(ws.id);
          restoreWorkspace.mutate(ws.id);
        },
      })),
      ...canvases.map((canvas) => ({
        id: canvas.id,
        kind: "canvas" as const,
        name: canvas.name || "Untitled",
        where:
          workspaceNames.get(canvas.workspace_id ?? "") ??
          (canvas.parent_archived ? "an archived workspace" : undefined),
        archivedAt: canvas.archived_at,
        blockedReason: canvas.parent_archived
          ? "Restore its workspace first"
          : undefined,
        onRestore: () => {
          setRestoringId(canvas.id);
          restoreCanvas.mutate(canvas.id);
        },
      })),
      ...chats.map((chat) => ({
        id: chat.id,
        kind: "chat" as const,
        name: chat.name || "Untitled chat",
        where:
          workspaceNames.get(chat.workspace_id ?? "") ??
          (chat.parent_archived ? "an archived workspace" : undefined),
        archivedAt: chat.archived_at,
        blockedReason: chat.parent_archived
          ? "Restore what it belonged to first"
          : undefined,
        onRestore: () => {
          setRestoringId(chat.id);
          restoreChat.mutate(chat.id);
        },
      })),
      ...files.map((file) => {
        const archivedAt = file.last_modified_at ?? file.updated_at;
        return {
          id: file.id,
          kind: "file" as const,
          name:
            file.path?.split("/").filter(Boolean).pop() || file.path || "File",
          where: workspaceNames.get(file.workspace_id ?? ""),
          archivedAt,
          extraMeta: fileDaysLeft(archivedAt),
          icon: (
            <FileRecordTypeIcon
              contentType={file.content_type}
              originType={file.origin_type}
              className="size-4"
            />
          ),
          onRestore: () => {
            setRestoringId(file.id);
            recoverFile.mutate(file.id);
          },
        };
      }),
      ...skills.map((skill) => ({
        id: skill.id,
        kind: "skill" as const,
        name: skill.name || "Untitled",
        where: "Files",
        archivedAt: skill.archived_at,
        onRestore: () => {
          setRestoringId(skill.id);
          restoreSkill.mutate({ skillId: skill.id });
        },
      })),
    ];

    // Most recently archived first — the reason you came here.
    return all.sort((a, b) => {
      const at = a.archivedAt ? Date.parse(a.archivedAt) : 0;
      const bt = b.archivedAt ? Date.parse(b.archivedAt) : 0;
      return bt - at;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    workspacesQuery.data,
    canvasesQuery.data,
    chatsQuery.data,
    filesQuery.data,
    skillsQuery.data,
    workspaceNames,
  ]);

  const queries = [
    workspacesQuery,
    canvasesQuery,
    chatsQuery,
    filesQuery,
    skillsQuery,
  ];
  const isLoading = queries.some((q) => q.isLoading);
  const hasMore = queries.some((q) => q.hasNextPage);
  const isFetchingMore = queries.some((q) => q.isFetchingNextPage);

  const inScope =
    scope === "All"
      ? entries
      : entries.filter((entry) => KIND_META[entry.kind].scope === scope);
  const search = query.trim().toLowerCase();
  const visible = search
    ? inScope.filter((entry) => entry.name.toLowerCase().includes(search))
    : inScope;

  const isRestoring = (entry: ArchiveEntry) =>
    restoringId === entry.id &&
    (restoreWorkspace.isPending ||
      restoreCanvas.isPending ||
      restoreChat.isPending ||
      recoverFile.isPending ||
      restoreSkill.isPending);

  return (
    <PaneBody>
      <PaneHead
        value={query}
        onChange={setQuery}
        placeholder="Search the archive"
      />

      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <Spinner />
        </div>
      ) : visible.length === 0 ? (
        search ? (
          <EmptySearch
            query={query}
            noun="archived items"
            onClear={() => setQuery("")}
          />
        ) : (
          <EmptyState
            title="Nothing archived here."
            description="Archived work keeps its contents and can be restored any time."
          />
        )
      ) : (
        <>
          <div className="flex flex-col gap-0.5">
            {visible.map((entry) => {
              const meta = [
                KIND_META[entry.kind].noun,
                entry.where,
                archivedAgo(entry.archivedAt),
                entry.extraMeta,
              ]
                .filter(Boolean)
                .join(" · ");

              return (
                <Row
                  key={`${entry.kind}-${entry.id}`}
                  tile={entry.icon ?? KIND_META[entry.kind].icon}
                  name={entry.name}
                  meta={meta}
                >
                  {entry.blockedReason ? (
                    // No disabled button — a greyed affordance reads as broken
                    // rather than as "not yet".
                    <span
                      className="inline-flex shrink-0 items-center gap-1 px-2 text-sm text-n-50"
                      title={entry.blockedReason}
                    >
                      <Lock className="size-3" />
                      {entry.blockedReason}
                    </span>
                  ) : (
                    <Button
                      variant="ghost"
                      size="sm"
                      className={cn("shrink-0", chipButtonClassName)}
                      disabled={isRestoring(entry)}
                      isLoading={isRestoring(entry)}
                      onClick={entry.onRestore}
                    >
                      <ArchiveRestore />
                      Restore
                    </Button>
                  )}
                </Row>
              );
            })}
          </div>

          {hasMore && (
            <div className="flex justify-center pt-4">
              <Button
                variant="ghost"
                size="sm"
                disabled={isFetchingMore}
                onClick={() => {
                  for (const query of queries) {
                    if (query.hasNextPage) query.fetchNextPage();
                  }
                }}
              >
                {isFetchingMore ? "Loading…" : "Load more"}
              </Button>
            </div>
          )}
        </>
      )}

      <SkillNameConflictDialog
        open={skillConflict !== null}
        originalName={skillConflict?.originalName ?? ""}
        suggestedName={skillConflict?.suggestedName ?? ""}
        isPending={restoreSkill.isPending}
        onCancel={() => setSkillConflict(null)}
        onKeepBoth={() => {
          if (!skillConflict) return;
          restoreSkill.mutate({
            skillId: skillConflict.skillId,
            newName: skillConflict.suggestedName,
          });
        }}
      />
    </PaneBody>
  );
}
